import { requireSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { normalizeProductCode } from '../../domain/catalogValidation';
import { parseCsvBooleanStrict, parseCsvItemTypeStrict } from '../../domain/csv/csvParseHelpers';
import { type MasterMatchCandidate } from '../../domain/csv/masterMatching';
import {
  createProduct,
  deactivateProduct,
  reactivateProduct,
  updateProduct,
} from '../productsRepo';
import { resolveProductRowForMutation } from '../webCatalogMutationResolve';
import {
  createVariant,
  updateVariant,
  listVariantsForProduct,
} from '../productVariantsRepo';
import {
  normCsvText,
  parseBarcodeForCsvCell,
  productCsvExistingMatchesResolvedCommit,
} from './catalogCsvProductDelta';
import {
  logCatalogCsvBarcodeDebug,
  resolveBrandFromRecord,
  resolveCategoryFromRecord,
  resolveSupplierFromRecord,
  unmatchedMasterMessage,
  CSV_UNMATCHED_BRAND_MESSAGE,
  CSV_UNMATCHED_CATEGORY_MESSAGE,
  CSV_UNMATCHED_SUPPLIER_MESSAGE,
  type ParsedRow,
} from './catalogCsvImportHelpers';
import { findProductBySku } from './catalogCsvImportLookup';
import { parseVariantFromCsvRow } from './catalogCsvVariantParse';

export async function commitProductRow(
  record: ParsedRow,
  ctx: {
    categoryCandidates: MasterMatchCandidate[];
    brandCandidates: MasterMatchCandidate[];
    supplierCandidates: MasterMatchCandidate[];
    performedByUserId?: string | null;
  }
): Promise<{ outcome: 'created' | 'updated' | 'skipped'; productId: string | null }> {
  await requireSessionOrganizationId();
  const sku = normalizeProductCode(record.sku?.trim() ?? '');
  const existing = await findProductBySku(sku);

  // #712: El blocker de variantes activas fue removido.
  // Ahora el CSV soporta productos con variantes (columnas variante_*).

  let categoryId: string | null = existing?.categoryId ?? null;
  if (record.categoria_id?.trim() || record.categoria?.trim()) {
    const catMatch = resolveCategoryFromRecord(record, ctx.categoryCandidates);
    const catErr = unmatchedMasterMessage(catMatch, true, CSV_UNMATCHED_CATEGORY_MESSAGE);
    if (catErr) throw new Error(catErr);
    categoryId = catMatch.id;
  }

  let brandId: string | null = existing?.brandId ?? null;
  if (record.marca_id?.trim() || record.marca?.trim()) {
    const brandMatch = resolveBrandFromRecord(record, ctx.brandCandidates);
    const brandErr = unmatchedMasterMessage(brandMatch, true, CSV_UNMATCHED_BRAND_MESSAGE);
    if (brandErr) throw new Error(brandErr);
    brandId = brandMatch.id;
  }

  let preferredSupplierId: string | null = existing?.preferredSupplierId ?? null;
  if (record.proveedor_preferido_id?.trim() || record.proveedor_preferido?.trim()) {
    const supMatch = resolveSupplierFromRecord(record, ctx.supplierCandidates);
    const supErr = unmatchedMasterMessage(supMatch, true, CSV_UNMATCHED_SUPPLIER_MESSAGE);
    if (supErr) throw new Error(supErr);
    preferredSupplierId = supMatch.id;
  }

  const tipoCell = parseCsvItemTypeStrict(record.tipo);
  if (tipoCell.error) throw new Error(tipoCell.error);
  const itemType = tipoCell.value ?? (existing?.itemType === 'service' ? 'service' : 'physical');
  const nombreRaw = record.nombre?.trim();
  if (!existing && !nombreRaw) {
    throw new Error('Nombre obligatorio.');
  }
  const nombre = (nombreRaw ? normCsvText(nombreRaw) : null) || existing?.name || '';
  const precioRaw = record.precio?.trim();
  const price =
    precioRaw !== '' && precioRaw != null
      ? (parseDecimalInput(precioRaw) ?? NaN)
      : (existing?.price ?? NaN);

  if (!nombre) throw new Error('Nombre obligatorio.');
  if (Number.isNaN(price) || price <= 0) throw new Error('Precio inválido.');

  const barcodeParsed = parseBarcodeForCsvCell(record.codigo_barras, { isUpdate: Boolean(existing) });
  logCatalogCsvBarcodeDebug(sku, 'commit', record.codigo_barras, {
    parse: barcodeParsed,
    existingDbBarcode: existing?.barcode ?? null,
  });
  if (barcodeParsed.error) throw new Error(barcodeParsed.error);
  const barcodeForMutation =
    barcodeParsed.barcode != null ? barcodeParsed.barcode : undefined;
  const nextBarcode =
    barcodeForMutation !== undefined ? barcodeForMutation : (existing?.barcode ?? null);

  const internalNotes =
    record.notas?.trim() !== '' && record.notas != null
      ? record.notas.trim()
      : existing?.internalNotes ?? null;
  const supplierItemCode =
    record.codigo_proveedor?.trim() !== '' && record.codigo_proveedor != null
      ? record.codigo_proveedor.trim()
      : existing?.supplierItemCode ?? null;

  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) throw new Error(activoCell.error);
  const activeFlag = activoCell.value;
  const mutationOpts = { performedByUserId: ctx.performedByUserId ?? null };

  if (existing) {
    const changeCtx = {
      categoryCandidates: ctx.categoryCandidates,
      brandCandidates: ctx.brandCandidates,
      supplierCandidates: ctx.supplierCandidates,
    };
    if (
      productCsvExistingMatchesResolvedCommit(
        existing,
        {
          name: nombre,
          price,
          sku,
          categoryId,
          brandId,
          preferredSupplierId,
          itemType,
          barcode: nextBarcode,
          internalNotes,
          supplierItemCode,
          activeFlag,
        },
        changeCtx
      )
    ) {
      return { outcome: 'skipped', productId: existing?.id ?? null };
    }
  }

  if (!existing) {
    const newProductId = await createProduct(
      {
        name: nombre,
        price,
        sku,
        categoryId,
        barcode: barcodeForMutation,
        itemType,
        brandId,
        internalNotes,
        preferredSupplierId,
        supplierItemCode,
        isActive: activeFlag ?? true,
      },
      mutationOpts
    );
    return { outcome: 'created', productId: newProductId };
  }

  // Preservar campos no cubiertos por el CSV para que updateProduct
  // no los pise con defaults (imageUrl, unitOfMeasure, kitchenDestination, etc.)
  const fullExisting = await resolveProductRowForMutation(existing.id);
  const preserved: Record<string, unknown> = {};
  if (fullExisting) {
    if (fullExisting.unit_of_measure) preserved.unitOfMeasure = fullExisting.unit_of_measure as 'unit' | 'kg' | 'g' | 'l' | 'ml' | 'hour' | 'm' | 'cm';
    if (fullExisting.duration_minutes != null) preserved.durationMinutes = fullExisting.duration_minutes;
    if (fullExisting.buffer_before_minutes != null) preserved.bufferBeforeMinutes = fullExisting.buffer_before_minutes;
    if (fullExisting.buffer_after_minutes != null) preserved.bufferAfterMinutes = fullExisting.buffer_after_minutes;
    if (fullExisting.image_url) preserved.imageUrl = fullExisting.image_url;
    if (fullExisting.public_description) preserved.publicDescription = fullExisting.public_description;
    if (fullExisting.is_public === 0) preserved.isPublic = false;
    if (fullExisting.active_ingredient) preserved.activeIngredient = fullExisting.active_ingredient;
    if (fullExisting.usage_tags && fullExisting.usage_tags !== '[]') {
      try { preserved.usageTags = JSON.parse(fullExisting.usage_tags); } catch { /* ignore */ }
    }
    if (fullExisting.booking_mode === 'quote_required') preserved.bookingMode = 'quote_required';
    if (fullExisting.evaluation_product_id) preserved.evaluationProductId = fullExisting.evaluation_product_id;
    if (fullExisting.booking_payment_mode && fullExisting.booking_payment_mode !== 'inherit') preserved.bookingPaymentMode = fullExisting.booking_payment_mode as 'none' | 'deposit' | 'full';
    if (fullExisting.booking_deposit_amount != null) preserved.bookingDepositAmount = fullExisting.booking_deposit_amount;
    if (fullExisting.booking_deposit_percent != null) preserved.bookingDepositPercent = fullExisting.booking_deposit_percent;
    if (fullExisting.kitchen_destination && fullExisting.kitchen_destination !== 'kitchen') preserved.kitchenDestination = fullExisting.kitchen_destination as 'bar' | 'none';
    if (fullExisting.tax_class) preserved.taxClass = fullExisting.tax_class;
    if (fullExisting.menu_available === 0) preserved.menuAvailable = false;
    if (fullExisting.parent_product_id) preserved.parentProductId = fullExisting.parent_product_id;
  }

  const didUpdate = await updateProduct(
    existing.id,
    {
      name: nombre,
      price,
      sku,
      categoryId,
      barcode: barcodeForMutation,
      itemType: itemType as 'physical' | 'service',
      brandId,
      internalNotes,
      preferredSupplierId,
      supplierItemCode,
      ...preserved,
    },
    mutationOpts
  );

  if (!didUpdate && activeFlag === undefined) {
    return { outcome: 'skipped', productId: existing.id };
  }

  if (activeFlag !== undefined) {
    if (activeFlag && existing.isActive === 0) {
      await reactivateProduct(existing.id, mutationOpts);
    } else if (!activeFlag && existing.isActive !== 0) {
      await deactivateProduct(existing.id, mutationOpts);
    }
  }

  return { outcome: 'updated', productId: existing.id };
}

/**
 * #712: Persiste variantes para un producto a partir de un grupo de filas CSV.
 * - La primera fila del grupo define/actualiza el parent.
 * - Todas las filas del grupo con columnas variante_* generan variantes.
 * - Si el producto ya tiene variantes, se matchean por atributos (cellKey) y se actualizan.
 * - Las variantes existentes no presentes en el CSV se desactivan.
 */
export async function commitProductGroup(
  rows: Array<{ rowNumber: number; record: ParsedRow }>,
  ctx: {
    categoryCandidates: MasterMatchCandidate[];
    brandCandidates: MasterMatchCandidate[];
    supplierCandidates: MasterMatchCandidate[];
    performedByUserId?: string | null;
    variantsEnabled?: boolean;
  }
): Promise<Array<{ rowNumber: number; outcome: 'created' | 'updated' | 'skipped' | 'error'; message?: string }>> {
  const results: Array<{ rowNumber: number; outcome: 'created' | 'updated' | 'skipped' | 'error'; message?: string }> = [];
  if (rows.length === 0) return results;

  // 1. Commit del parent usando la primera fila.
  const firstRow = rows[0];
  let parentResult: { outcome: 'created' | 'updated' | 'skipped'; productId: string | null };
  try {
    parentResult = await commitProductRow(firstRow.record, ctx);
  } catch (e) {
    results.push({ rowNumber: firstRow.rowNumber, outcome: 'error', message: e instanceof Error ? e.message : 'Error parent.' });
    // Si el parent falla, no tiene sentido procesar variantes.
    for (let i = 1; i < rows.length; i += 1) {
      results.push({ rowNumber: rows[i].rowNumber, outcome: 'error', message: 'Parent falló.' });
    }
    return results;
  }

  results.push({ rowNumber: firstRow.rowNumber, outcome: parentResult.outcome });
  const productId = parentResult.productId;
  if (!productId) {
    // Sin productId no podemos crear variantes.
    for (let i = 1; i < rows.length; i += 1) {
      results.push({ rowNumber: rows[i].rowNumber, outcome: 'skipped' });
    }
    return results;
  }

  // 2. Verificar si las variantes están habilitadas.
  const variantsEnabled = ctx.variantsEnabled ?? false;
  if (!variantsEnabled) {
    // Si hay filas con columnas de variante pero el feature está deshabilitado, error.
    for (let i = 1; i < rows.length; i += 1) {
      const variantDraft = tryParseVariant(rows[i].record, 0);
      if (variantDraft) {
        results.push({ rowNumber: rows[i].rowNumber, outcome: 'error', message: 'Variantes deshabilitadas.' });
      } else {
        results.push({ rowNumber: rows[i].rowNumber, outcome: 'skipped' });
      }
    }
    return results;
  }

  // 3. Parsear variantes de todas las filas del grupo.
  const parentPrice = parseParentPrice(firstRow.record);
  const variantDrafts: Array<{ rowNumber: number; draft: import('../../domain/productDraft').ProductVariantDraft }> = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    try {
      const draft = parseVariantFromCsvRow(row.record, parentPrice);
      if (draft) {
        variantDrafts.push({ rowNumber: row.rowNumber, draft });
      } else if (i > 0) {
        // Filas adicionales sin variante = solo actualizan parent (ya hecho).
        results.push({ rowNumber: row.rowNumber, outcome: 'skipped' });
      }
    } catch (e) {
      results.push({ rowNumber: row.rowNumber, outcome: 'error', message: e instanceof Error ? e.message : 'Variante inválida.' });
    }
  }

  if (variantDrafts.length === 0) {
    return results;
  }

  // 4. Cargar variantes existentes del producto (solo activas).
  const existingVariants = await listVariantsForProduct(productId);

  // 5. Para cada draft, matchear por atributos o crear nuevo.
  for (const { rowNumber, draft } of variantDrafts) {
    try {
      const match = existingVariants.find((v) => attributesEqual(v.attributes, draft.attributes));

      const priceNum = draft.price ? Number(draft.price) : null;
      if (draft.price && (Number.isNaN(priceNum) || priceNum! < 0)) {
        throw new Error('variante_precio inválido.');
      }

      if (match) {
        await updateVariant(match.id, {
          productId,
          nameSuffix: draft.nameSuffix,
          sku: draft.sku || null,
          barcode: draft.barcode || null,
          attributes: draft.attributes,
          price: priceNum,
        });
        results.push({ rowNumber, outcome: 'updated' });
      } else {
        await createVariant({
          productId,
          nameSuffix: draft.nameSuffix,
          sku: draft.sku || null,
          barcode: draft.barcode || null,
          attributes: draft.attributes,
          price: priceNum,
        });
        results.push({ rowNumber, outcome: 'created' });
      }
    } catch (e) {
      results.push({ rowNumber, outcome: 'error', message: e instanceof Error ? e.message : 'No se pudo guardar la variante.' });
    }
  }

  // Nota: NO desactivamos variantes existentes que no están en el CSV.
  // El CSV es aditivo por defecto — el usuario puede tener variantes creadas desde la UI
  // que no están en el archivo. Desactivarlas silenciosamente sería destructivo.
  // Para remover variantes, usar la UI de editar producto.

  return results;
}

function parseParentPrice(record: ParsedRow): number {
  const raw = record.precio?.trim();
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

function tryParseVariant(record: ParsedRow, parentPrice: number): import('../../domain/productDraft').ProductVariantDraft | null {
  try {
    return parseVariantFromCsvRow(record, parentPrice);
  } catch {
    return null;
  }
}

function attributesEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  for (let i = 0; i < aKeys.length; i += 1) {
    if (aKeys[i] !== bKeys[i]) return false;
    if (a[aKeys[i]] !== b[bKeys[i]]) return false;
  }
  return true;
}
